import React, { useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faImage, faMicrophone, faStop } from "@fortawesome/free-solid-svg-icons";
import { pickAudioFormat } from "../../helpers/uploadChatMedia";

const MAX_IMAGE_BYTES = 8 * 1024 * 1024;   // Messenger's image limit
const MAX_RECORD_SEC = 120;

const btn = (disabled, active) => ({
    display: "flex", alignItems: "center", justifyContent: "center",
    width: 42, flexShrink: 0, borderRadius: 8, fontSize: 18,
    border: "1px solid #E0E0EA", background: active ? "#E53935" : "#FFF", color: active ? "#FFF" : "#555",
    cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.5 : 1,
});

/**
 * Pick an image or record a voice clip. Nothing is sent from here: the result is handed to
 * `onPick` as { type, blob, ext, contentType, previewUrl }, the inbox shows it above the text
 * box, and the send button sends it.
 */
export default function ChatMediaButtons({ disabled, onPick, onError }) {
    const fileRef = useRef(null);
    const recorder = useRef(null);
    const timer = useRef(null);
    const [recording, setRecording] = useState(false);
    const [seconds, setSeconds] = useState(0);

    //* Leaving the screen mid-recording must release the microphone.
    useEffect(() => () => stopTracks(), []);

    const stopTracks = () => {
        clearInterval(timer.current);
        const r = recorder.current;
        if (r && r.state !== "inactive") { r.onstop = null; r.stop(); }
        if (r && r.stream) r.stream.getTracks().forEach((tr) => tr.stop());
        recorder.current = null;
    };

    const onImage = (e) => {
        const file = e.target.files && e.target.files[0];
        e.target.value = "";
        if (!file) return;
        if (!file.type.startsWith("image/")) return onError?.("ກະລຸນາເລືອກໄຟລ໌ຮູບພາບ");
        if (file.size > MAX_IMAGE_BYTES) return onError?.("ຮູບໃຫຍ່ເກີນ 8MB");
        const ext = (file.name.split(".").pop() || "jpg").toLowerCase();
        onPick({ type: "image", blob: file, ext, contentType: file.type, previewUrl: URL.createObjectURL(file) });
    };

    const startRecording = async () => {
        const fmt = pickAudioFormat();
        if (!fmt || !navigator.mediaDevices?.getUserMedia) return onError?.("ບຣາວເຊີນີ້ບັນທຶກສຽງບໍ່ໄດ້");
        let stream;
        try {
            stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        } catch (e) {
            return onError?.("ກະລຸນາອະນຸຍາດໃຫ້ໃຊ້ໄມໂຄຣໂຟນ");
        }
        const chunks = [];
        const r = new MediaRecorder(stream, { mimeType: fmt.mime });
        r.ondataavailable = (ev) => { if (ev.data && ev.data.size) chunks.push(ev.data); };
        r.onstop = () => {
            stream.getTracks().forEach((tr) => tr.stop());
            const contentType = fmt.mime.split(";")[0];
            const blob = new Blob(chunks, { type: contentType });
            if (blob.size) onPick({ type: "audio", blob, ext: fmt.ext, contentType, previewUrl: URL.createObjectURL(blob) });
        };
        recorder.current = r;
        r.start();
        setSeconds(0);
        setRecording(true);
        timer.current = setInterval(() => setSeconds((s) => s + 1), 1000);
    };

    const stopRecording = () => {
        clearInterval(timer.current);
        const r = recorder.current;
        if (r && r.state !== "inactive") r.stop();
        recorder.current = null;
        setRecording(false);
    };

    useEffect(() => { if (recording && seconds >= MAX_RECORD_SEC) stopRecording(); }, [recording, seconds]);

    const mmss = `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;

    return (
        <>
            <input ref={fileRef} type="file" accept="image/*" style={{ display: "none" }} onChange={onImage} />
            <div
                title="ສົ່ງຮູບ"
                style={btn(disabled || recording)}
                onClick={() => !disabled && !recording && fileRef.current?.click()}
            >
                <FontAwesomeIcon icon={faImage} />
            </div>
            <div
                title={recording ? "ຢຸດບັນທຶກ" : "ບັນທຶກສຽງ"}
                style={{ ...btn(disabled, recording), width: recording ? 78 : 42, gap: 6, fontSize: recording ? 14 : 18 }}
                onClick={() => { if (disabled) return; if (recording) stopRecording(); else startRecording(); }}
            >
                <FontAwesomeIcon icon={recording ? faStop : faMicrophone} />
                {recording && <span>{mmss}</span>}
            </div>
        </>
    );
}
